import { Controller } from '@hotwired/stimulus'
import { Chart } from 'chart.js/auto'
import { barChartOptions, doughnutChartOptions } from '~/javascript/charts/chart_config'

// Chart colors following BodyOS style guide
// Uses Slate (blue-gray) scale + semantic colors for diversity
const chartColors = {
  // Platform colors (semantic: info for iOS, success for Android)
  platform: {
    iphone: '#0369a1', // info-base (dark sky blue)
    android: '#166534', // success-base (dark green)
    unknown: '#64748b' // slate-500
  },
  // Multi-item palette using slate scale + semantic accents
  // Provides visual diversity while staying on-brand
  palette: [
    '#0f172a', // slate-900 (primary dark)
    '#334155', // slate-700
    '#0369a1', // info-base
    '#64748b', // slate-500
    '#166534', // success-base
    '#1e293b', // slate-800
    '#854d0e', // warning-base
    '#475569', // slate-600
    '#991b1b', // danger-dark
    '#94a3b8' // slate-400
  ],
  // Weekly chart
  weekly: '#1e293b' // slate-800
}

export default class extends Controller {
  static values = {
    platformDistribution: Object,
    weeklyOptins: Object,
    topWearables: Object,
    topGoals: Object
  }

  static targets = ['platform', 'weekly', 'wearables', 'goals']

  connect() {
    this.charts = []
    this.initCharts()
  }

  disconnect() {
    this.charts.forEach((chart) => chart.destroy())
  }

  initCharts() {
    this.initPlatformChart()
    this.initWeeklyChart()
    this.initWearablesChart()
    this.initGoalsChart()
  }

  initPlatformChart() {
    if (!this.hasPlatformTarget) return

    const data = this.platformDistributionValue
    // Map platform labels to semantic colors
    const platformColors = data.labels.map((label) => {
      const key = label.toLowerCase()
      return chartColors.platform[key] || chartColors.platform.unknown
    })

    const chart = new Chart(this.platformTarget, {
      type: 'doughnut',
      data: {
        labels: data.labels,
        datasets: [{
          data: data.data,
          backgroundColor: platformColors,
          borderColor: '#ffffff',
          borderWidth: 3
        }]
      },
      options: doughnutChartOptions
    })
    this.charts.push(chart)
  }

  initWeeklyChart() {
    if (!this.hasWeeklyTarget) return

    const data = this.weeklyOptinsValue
    const chart = new Chart(this.weeklyTarget, {
      type: 'bar',
      data: {
        labels: data.labels,
        datasets: [{
          data: data.data,
          backgroundColor: chartColors.weekly,
          borderColor: '#0f172a', // slate-900 border
          borderWidth: 2,
          borderRadius: 4
        }]
      },
      options: barChartOptions
    })
    this.charts.push(chart)
  }

  initWearablesChart() {
    if (!this.hasWearablesTarget) return

    const data = this.topWearablesValue
    // Each wearable gets its own color from the slate/semantic palette
    const wearableColors = data.labels.map((_, i) => chartColors.palette[i % chartColors.palette.length])

    const chart = new Chart(this.wearablesTarget, {
      type: 'bar',
      data: {
        labels: data.labels,
        datasets: [{
          data: data.data,
          backgroundColor: wearableColors,
          borderColor: wearableColors.map((c) => c), // Same as fill
          borderWidth: 2,
          borderRadius: 4
        }]
      },
      options: {
        ...barChartOptions,
        indexAxis: 'y',
        scales: {
          ...barChartOptions.scales,
          x: {
            ...barChartOptions.scales.y,
            beginAtZero: true
          },
          y: {
            ...barChartOptions.scales.x,
            grid: { display: false }
          }
        }
      }
    })
    this.charts.push(chart)
  }

  initGoalsChart() {
    if (!this.hasGoalsTarget) return

    const data = this.topGoalsValue
    // Each goal gets its own color, offset from wearables palette
    const goalColors = data.labels.map((_, i) => chartColors.palette[(i + 3) % chartColors.palette.length])

    const chart = new Chart(this.goalsTarget, {
      type: 'bar',
      data: {
        labels: data.labels,
        datasets: [{
          data: data.data,
          backgroundColor: goalColors,
          borderColor: goalColors.map((c) => c),
          borderWidth: 2,
          borderRadius: 4
        }]
      },
      options: {
        ...barChartOptions,
        indexAxis: 'y',
        scales: {
          ...barChartOptions.scales,
          x: {
            ...barChartOptions.scales.y,
            beginAtZero: true
          },
          y: {
            ...barChartOptions.scales.x,
            grid: { display: false }
          }
        }
      }
    })
    this.charts.push(chart)
  }
}
